/**
 * PATH: apps/billing/src/lib/setupApi.ts
 * FILE_ID: BILLING-LIB-SETUPAPI-001
 * PURPOSE: Bootstrap the @quotech/api client for the Billing hub.
 *
 * WHY: Every hub re-runs setupHubApi to wire its own auth store + tenant
 *      context into the shared axios client. This file is called once
 *      from main.tsx before React renders.
 *
 * EXPORTS:
 *   - setupBillingApi() / initializeApi() : one-shot bootstrap (call from main.tsx)
 *   - getConfiguredClient() : read-only access to the configured axios instance
 *   - apiClient : the default client (re-export for back-compat with older imports)
 *   - billingApi : a thin convenience wrapper around the bills API endpoints
 *                  (apps/billing prefers the TanStack-Query hooks in
 *                  ../hooks/useBills.ts which auto-handle caching / invalidation;
 *                  use billingApi only for one-off imperative calls).
 *
 * REMOVED 2026-05-03 (v2 master plan): aspirational /api/inbox/, /api/approvals/,
 * /api/automation/rules/, /api/integrations/, /api/dashboard/stats/ endpoints
 * that don't exist on the backend. The real paths are documented in
 * docs/architecture/billing/00_MASTER_PLAN.md and live under /api/bills/*.
 */
import { setupHubApi, getConfiguredClient, getDefaultClient } from '@quotech/api'
import { billsEndpoints } from '@quotech/bills'

export { getConfiguredClient }

/** Call once in main.tsx before rendering. */
export function initializeApi() {
  setupHubApi({ hubName: 'Billing' })
}

// Export with expected name for main.tsx
export const setupBillingApi = initializeApi

// Re-export configured client for backward compat
export const apiClient = getDefaultClient()

/**
 * Convenience wrapper around the bills API. Prefer the hooks in
 * ../hooks/useBills.ts (caching + automatic invalidation). This object
 * exists for imperative call sites (e.g. event handlers outside of
 * components, or one-off scripts) that don't want a React-Query
 * mutation lifecycle.
 *
 * Endpoint paths verified against `repos/quotech-backend/organisation/urls.py`:
 *   GET   /api/bills/                       -> BillListView
 *   GET   /api/bills/stats/                 -> BillStatsView
 *   GET   /api/bills/<billId>/              -> BillDetailView
 *   PATCH /api/bills/<billId>/              -> BillDetailView (partial update)
 *   POST  /api/bills/<billId>/approve/      -> BillApproveView
 *   POST  /api/bills/<billId>/reject/       -> BillRejectView
 *   POST  /api/bills/<billId>/link-job/     -> BillLinkJobView
 *   POST  /api/bills/<billId>/push-to-xero/    -> BillXeroPushView
 *   POST  /api/bills/extract/<emailId>/     -> BillExtractView
 *   POST  /api/bills/extract-pending/       -> BillExtractPendingView
 */
export const billingApi = {
  // List + detail
  getBills: (params?: { status?: string }) =>
    getDefaultClient().get(billsEndpoints.list({ status: params?.status as never })),
  getBill: (id: string) => getDefaultClient().get(billsEndpoints.detail(id)),
  getStats: () => getDefaultClient().get(billsEndpoints.stats()),

  // Mutations
  updateBill: (id: string, data: unknown) =>
    getDefaultClient().patch(billsEndpoints.update(id), data),
  approveBill: (id: string) => getDefaultClient().post(billsEndpoints.approve(id)),
  rejectBill: (id: string, reason?: string) =>
    getDefaultClient().post(billsEndpoints.reject(id), { reason: reason ?? '' }),
  linkJob: (id: string, jobId: number | null) =>
    getDefaultClient().post(billsEndpoints.linkJob(id), { job_id: jobId }),
  xeroPush: (id: string) => getDefaultClient().post(billsEndpoints.pushToXero(id)),

  // Extraction
  extractBillFromEmail: (emailId: string) =>
    getDefaultClient().post(billsEndpoints.extractFromEmail(emailId)),
  extractPending: (opts: { limit?: number; force?: boolean } = {}) =>
    getDefaultClient().post(billsEndpoints.extractPending(), opts),
}
